import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FaUsers, FaMoneyBillWave, FaGlobe, FaEye, FaPlus, FaUserEdit, FaChartLine } from "react-icons/fa";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { authFetch } from "../../utils/api/authFetch";
import { StatCard, Panel } from "./shell";

const naira = (n) => `₦${Number(n || 0).toLocaleString()}`;

// Studio home: a quick read on how the teacher's marketplace is doing —
// headline stats, a 6-month activity trend, top content, and shortcuts.
const StudioDashboard = ({ navigate }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await authFetch("/api/marketplace/analytics");
                const d = await res.json().catch(() => ({}));
                setData(d.data || null);
            } catch (err) {
                toast.error("Could not load your dashboard");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) return <p className="text-gray-500">Loading…</p>;
    const t = data?.totals || {};
    const trend = data?.trend || [];
    const hasTrend = trend.some((p) => p.subscribers || p.opens || p.completions);

    return (
        <div className="space-y-6">
            {t.price == null && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <span>Set your monthly price to start selling your lessons to subscribers.</span>
                    <button onClick={() => navigate("/teacher/marketplace/profile")} className="bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-semibold shrink-0">
                        Set price
                    </button>
                </div>
            )}

            {/* Headline stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <StatCard icon={FaUsers} label="Subscribers" value={t.subscribers ?? 0} sub="paying now" />
                <StatCard icon={FaMoneyBillWave} label="Monthly revenue" value={naira(t.monthlyRevenue)} sub="recurring" accent="text-green-600" />
                <StatCard icon={FaGlobe} label="On marketplace" value={t.listedContent ?? 0} sub={`of ${t.totalContent ?? 0} lessons`} accent="text-amber-600" />
                <StatCard icon={FaEye} label="Total opens" value={t.totalOpens ?? 0} sub={`${t.totalCompletions ?? 0} completed`} accent="text-indigo-600" />
            </div>

            {/* Activity trend */}
            <Panel title="Last 6 months" action={
                <button onClick={() => navigate("/teacher/marketplace/analytics")} className="text-blue-600 text-sm font-semibold flex items-center gap-1">
                    <FaChartLine size={12} /> Full analytics
                </button>
            }>
                {hasTrend ? (
                    <div className="h-56 sm:h-64 -ml-2">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={trend} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="gSubs" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#2563eb" stopOpacity={0.35} />
                                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="gOpens" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={28} />
                                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
                                <Area type="monotone" dataKey="subscribers" name="New subscribers" stroke="#2563eb" strokeWidth={2} fill="url(#gSubs)" />
                                <Area type="monotone" dataKey="opens" name="Lesson opens" stroke="#6366f1" strokeWidth={2} fill="url(#gOpens)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                ) : (
                    <p className="text-sm text-gray-400 py-10 text-center">No activity yet. Once students open your lessons and subscribe, trends show up here.</p>
                )}
            </Panel>

            {/* Top content + shortcuts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Panel title="Top content" className="lg:col-span-2">
                    {(data?.topContent || []).filter((c) => c.opens > 0).length === 0 ? (
                        <p className="text-sm text-gray-400 py-6 text-center">No opens yet.</p>
                    ) : (
                        <div className="space-y-2">
                            {data.topContent.filter((c) => c.opens > 0).slice(0, 5).map((c, i) => (
                                <div key={c.id} className="flex items-center gap-3 text-sm">
                                    <span className="w-5 text-gray-400 font-semibold">{i + 1}</span>
                                    <span className="flex-1 truncate text-gray-800">{c.title}</span>
                                    <span className="text-gray-500 shrink-0">{c.opens} opens · {c.completions} done</span>
                                </div>
                            ))}
                        </div>
                    )}
                </Panel>

                <div className="grid grid-cols-1 gap-3">
                    <button onClick={() => navigate("/teacher/marketplace/content")} className="bg-blue-600 text-white rounded-xl p-4 text-left font-semibold flex items-center gap-2 hover:bg-blue-700">
                        <FaPlus /> Create / manage content
                    </button>
                    <button onClick={() => navigate("/teacher/marketplace/profile")} className="bg-white border border-gray-200 rounded-xl p-4 text-left font-semibold text-gray-700 flex items-center gap-2 hover:bg-gray-50">
                        <FaUserEdit className="text-blue-600" /> Edit profile
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StudioDashboard;
